import React from 'react';
import { SideBar } from '../../sidebar';
import HeaderBanner from '../../../common/HeaderBanner';
import {
  Plus,
  Save,
  X,
  Key,
  Hash,
  FileText,
  Edit2,
  Trash2,
  Settings as SettingsIcon,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getAllSettings,
  createSetting,
  updateSetting,
  deleteSetting,
} from './services/SystemSettingsService';
import DataTable from 'react-data-table-component';
import { tableStyles } from '../../../../styles/components/js/tableStyle';

const TYPE_OPTIONS = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'json', label: 'JSON' },
];

const emptyForm = { key: '', value: '', type: 'string', description: '' };

export class SystemSettingsPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      settings: [],
      loading: false,
      submitting: false,
      hideSidebar: false,
      form_open: false,
      editing_key: null, // null = adding new, string = editing existing
      form: { ...emptyForm },
    };
  }

  componentDidMount() {
    this.loadSettings();
  }

  loadSettings = async () => {
    this.setState({ loading: true });
    try {
      const response = await getAllSettings();
      if (response.success && response.data) {
        this.setState({ settings: response.data });
      } else {
        toast.error('Failed to load settings');
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Error loading settings');
    } finally {
      this.setState({ loading: false });
    }
  };

  openAddForm = () => {
    this.setState({
      form_open: true,
      editing_key: null,
      form: { ...emptyForm },
    });
  };

  openEditForm = (setting) => {
    this.setState({
      form_open: true,
      editing_key: setting.key,
      form: {
        key: setting.key,
        value: setting.value ?? '',
        type: setting.type || 'string',
        description: setting.description || '',
      },
    });
  };

  closeForm = () => {
    this.setState({ form_open: false, editing_key: null, form: { ...emptyForm } });
  };

  handleInputChange = (e) => {
    const { name, value } = e.target;
    this.setState((prevState) => ({
      form: { ...prevState.form, [name]: value },
    }));
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const { form, editing_key } = this.state;

    if (!form.key || form.value === '') {
      toast.error('Key and value are required');
      return;
    }

    this.setState({ submitting: true });
    try {
      let response;
      if (editing_key) {
        response = await updateSetting(editing_key, {
          value: form.value,
          type: form.type,
          description: form.description,
        });
      } else {
        response = await createSetting(form);
      }

      if (response.status || response.success) {
        toast.success(response.message || 'Setting saved successfully');
        this.closeForm();
        this.loadSettings();
      } else {
        toast.error(response.message || 'Failed to save setting');
      }
    } catch (error) {
      console.error('Error saving setting:', error);
      toast.error('Something went wrong while saving the setting');
    } finally {
      this.setState({ submitting: false });
    }
  };

  handleDelete = async (setting) => {
    const confirmed = window.confirm(`Delete setting "${setting.key}"? This cannot be undone.`);
    if (!confirmed) return;

    try {
      const response = await deleteSetting(setting.key);
      if (response.status || response.success) {
        toast.success('Setting deleted');
        this.loadSettings();
      } else {
        toast.error(response.message || 'Failed to delete setting');
      }
    } catch (error) {
      console.error('Error deleting setting:', error);
      toast.error('Something went wrong while deleting the setting');
    }
  };

  getSettingsColumns() {
    return [
      {
        name: '#',
        width: '70px',
        minWidth: '70px',
        maxWidth: '70px',
        sortable: false,
        center: true,
        cell: (row, index) => (
          <div className="text-center" style={{ fontWeight: '500', color: '#495057' }}>
            {index + 1}
          </div>
        ),
      },
      {
        name: 'Key',
        selector: (row) => row.key,
        sortable: true,
        grow: 2,
        minWidth: '200px',
        cell: (row) => (
          <div
            style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#212529',
              fontFamily: 'monospace',
            }}
          >
            {row.key}
          </div>
        ),
      },
      {
        name: 'Value',
        selector: (row) => row.value,
        sortable: false,
        grow: 2,
        minWidth: '180px',
        cell: (row) => (
          <div
            style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#495057',
              fontFamily: 'monospace',
              wordBreak: 'break-word',
            }}
          >
            {String(row.value)}
          </div>
        ),
      },
      {
        name: 'Type',
        selector: (row) => row.type,
        sortable: true,
        width: '110px',
        minWidth: '110px',
        maxWidth: '110px',
        center: true,
        cell: (row) => (
          <span
            className="badge"
            style={{
              backgroundColor: '#6c757d',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '500',
            }}
          >
            {(row.type || 'string').toUpperCase()}
          </span>
        ),
      },
      {
        name: 'Description',
        selector: (row) => row.description,
        sortable: false,
        grow: 3,
        minWidth: '220px',
        cell: (row) => (
          <div style={{ fontSize: '14px', color: '#6c757d', lineHeight: '1.4' }}>
            {row.description || 'N/A'}
          </div>
        ),
      },
      {
        name: 'Actions',
        width: '130px',
        minWidth: '130px',
        maxWidth: '130px',
        center: true,
        cell: (row) => (
          <div className="btn-group btn-group-sm" style={{ gap: '4px' }}>
            <button
              type="button"
              className="btn-new btn-action"
              onClick={() => this.openEditForm(row)}
              title="Edit Setting"
            >
              <Edit2 size={14} />
            </button>
            <button
              type="button"
              className="btn-new btn-action"
              onClick={() => this.handleDelete(row)}
              title="Delete Setting"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ),
      },
    ];
  }

  render() {
    const { settings, submitting, form_open, editing_key, form } = this.state;

    return (
      <React.Fragment>
        <HeaderBanner
          title="System Settings"
          subtitle="Manage key/value configuration used across the platform"
        />

        <section className="mt-5 system-settings-page">
          <div className="container">
            <div className="row">
              <div className="col-xl-3 col-lg-12 col-md-12">
                {!this.state.hideSidebar && <SideBar />}
              </div>
              <div
                className={
                  this.state.hideSidebar
                    ? 'col-xl-12 col-lg-12 col-md-12'
                    : 'col-xl-9 col-lg-12 col-md-12'
                }
              >
                <div className="card">
                  <div className="card-header">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="expand-dashboard">
                        <span
                          style={{ fontSize: '1rem', marginRight: '0.5rem', cursor: 'pointer' }}
                          onClick={() => {
                            this.setState({
                              hideSidebar: !this.state.hideSidebar,
                            });
                          }}
                        >
                          {this.state.hideSidebar && <i className="icon-size-actual"></i>}
                          {!this.state.hideSidebar && <i className="icon-size-fullscreen"></i>}
                        </span>
                      </div>

                      <div className="d-flex align-items-center gap-3">
                      {!form_open && (
                        <button
                          type="button"
                          className="button1 button1-primary"
                          onClick={this.openAddForm}
                        >
                          <Plus size={16} />
                          Add Setting
                        </button>
                      )}
                    </div>
                    </div>
                    
                  </div>

                  <div className="card-body">
                    <div className="system-settings-details">
                      {/* Add / Edit Setting Section */}
                      {form_open && (
                        <div className="form-section">
                          <h3 className="section-title">
                            <SettingsIcon size={20} className="me-2" />
                            {editing_key ? 'Edit Setting' : 'Add New Setting'}
                          </h3>
                          <form onSubmit={this.handleSubmit}>
                            <div className="row">
                              <div className="col-md-6">
                                <div className="form-group">
                                  <label className="form-label">Key *</label>
                                  <div className="input-group">
                                    <span className="input-group-text">
                                      <Key size={16} />
                                    </span>
                                    <input
                                      type="text"
                                      name="key"
                                      value={form.key}
                                      onChange={this.handleInputChange}
                                      className="form-control"
                                      placeholder="e.g. discount_percentage"
                                      required
                                      disabled={submitting || !!editing_key}
                                    />
                                  </div>
                                </div>
                              </div>

                              <div className="col-md-6">
                                <div className="form-group">
                                  <label className="form-label">Value *</label>
                                  <div className="input-group">
                                    <span className="input-group-text">
                                      <Hash size={16} />
                                    </span>
                                    <input
                                      type="text"
                                      name="value"
                                      value={form.value}
                                      onChange={this.handleInputChange}
                                      className="form-control"
                                      placeholder="e.g. 25"
                                      required
                                      disabled={submitting}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-md-6">
                                <div className="form-group">
                                  <label className="form-label">Type</label>
                                  <select
                                    name="type"
                                    value={form.type}
                                    onChange={this.handleInputChange}
                                    className="form-control"
                                    disabled={submitting}
                                  >
                                    {TYPE_OPTIONS.map((opt) => (
                                      <option value={opt.value} key={opt.value}>
                                        {opt.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>

                              <div className="col-md-6">
                                <div className="form-group">
                                  <label className="form-label">Description</label>
                                  <div className="input-group">
                                    <span className="input-group-text">
                                      <FileText size={16} />
                                    </span>
                                    <input
                                      type="text"
                                      name="description"
                                      value={form.description}
                                      onChange={this.handleInputChange}
                                      className="form-control"
                                      placeholder="What this setting controls"
                                      disabled={submitting}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="d-flex justify-content-end gap-3 mt-3">
                              <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={this.closeForm}
                                disabled={submitting}
                              >
                                <X size={16} />
                                Cancel
                              </button>
                              <button type="submit" className="btn btn-primary" disabled={submitting}>
                                <Save size={16} />
                                {submitting
                                  ? 'Saving...'
                                  : editing_key
                                  ? 'Update Setting'
                                  : 'Save Setting'}
                              </button>
                            </div>
                          </form>
                        </div>
                      )}

                      {/* All Settings DataTable */}
                      <div className="form-section">
                        <h3 className="section-title">
                          <SettingsIcon size={20} className="me-2" />
                          All Settings ({settings.length})
                        </h3>
                        <div
                          className="table-container"
                          style={{ borderRadius: '8px', overflow: 'hidden' }}
                        >
                          <DataTable
                            columns={this.getSettingsColumns()}
                            data={settings}
                            customStyles={tableStyles}
                            progressPending={this.state.loading}
                            highlightOnHover
                            noHeader
                            responsive
                            noDataComponent={
                              <div className="py-4 text-center text-muted">
                                No settings yet - add your first one.
                              </div>
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </React.Fragment>
    );
  }
}